import { createTheme } from "@mui/material/styles";
import type { PaletteMode } from "@mui/material";

// ---------------------------------------------------------------------------
// ProText design system
// A light/dark, refined workspace aesthetic inspired by Linear / Vercel /
// Notion. Brand orange is reserved as an accent (logo, slider, export CTA);
// neutral graphite/zinc carries the rest of the UI for calm hierarchy.
// ---------------------------------------------------------------------------

const SANS = [
  "Inter",
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  "Roboto",
  "Helvetica",
  "Arial",
  "sans-serif",
].join(",");

const graphite = "#18181B"; // light-mode text + neutral primary
const brand = "#EA580C"; // vivid brand orange — accents / non-text surfaces
const brandStrong = "#C2410C"; // AA-safe fill for white button text (5.2:1)

export function createAppTheme(mode: PaletteMode) {
  const isDark = mode === "dark";
  const divider = isDark ? "#27272A" : "#EBEBE8";
  const inputBorder = isDark ? "#3F3F46" : "#E2E2DF";

  return createTheme({
    palette: {
      mode,
      primary: isDark
        ? { main: "#FAFAFA", contrastText: "#18181B" }
        : { main: graphite, contrastText: "#FFFFFF" },
      secondary: { main: brandStrong, light: brand, contrastText: "#FFFFFF" },
      background: isDark
        ? { default: "#0C0C0E", paper: "#161618" }
        : { default: "#FAFAF9", paper: "#FFFFFF" },
      text: isDark
        ? { primary: "#F4F4F5", secondary: "#A1A1AA", disabled: "#52525B" }
        : { primary: graphite, secondary: "#6B7280", disabled: "#A1A1AA" },
      divider,
      success: { main: isDark ? "#22C55E" : "#15803D" },
      warning: { main: brandStrong },
      error: { main: isDark ? "#F87171" : "#DC2626" },
    },
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: SANS,
      h6: { fontWeight: 700, fontSize: "1.0625rem", letterSpacing: "-0.01em" },
      subtitle1: { fontWeight: 600, letterSpacing: "-0.01em" },
      subtitle2: { fontWeight: 600, fontSize: "0.8125rem" },
      body1: { fontSize: "0.9375rem" },
      body2: { fontSize: "0.875rem" },
      caption: { fontSize: "0.75rem" },
      button: { textTransform: "none", fontWeight: 600, letterSpacing: 0 },
    },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 8, paddingTop: 8, paddingBottom: 8, paddingLeft: 16, paddingRight: 16 },
          sizeLarge: { paddingTop: 10, paddingBottom: 10 },
          containedPrimary: { "&:hover": { backgroundColor: isDark ? "#E4E4E7" : "#000000" } },
        },
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: { backgroundImage: "none" } },
      },
      MuiSlider: {
        styleOverrides: {
          root: { color: brand, height: 6 },
          rail: { opacity: isDark ? 0.3 : 0.22 },
          thumb: {
            width: 16,
            height: 16,
            "&:hover, &.Mui-focusVisible": { boxShadow: "0 0 0 6px rgba(234,88,12,0.16)" },
            "&.Mui-active": { boxShadow: "0 0 0 10px rgba(234,88,12,0.18)" },
          },
        },
      },
      MuiDialog: {
        styleOverrides: { paper: { borderRadius: 16, border: `1px solid ${divider}` } },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          notchedOutline: { borderColor: inputBorder },
          root: {
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: brand, borderWidth: 1.5 },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: isDark ? "#3F3F46" : graphite,
            fontSize: 12,
            borderRadius: 6,
            padding: "6px 8px",
          },
        },
      },
    },
  });
}

export default createAppTheme("light");
